import { useState, useEffect, useCallback, useRef } from 'react'
import {
  doc,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore'
import { db } from '../firebase/firebaseConfig'
import { useAuth } from '../components/auth/AuthProvider'

export function useFavorites() {
  const { isAuthenticated, user } = useAuth()
  const [favorites, setFavorites] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Ref to always have current favorites in callbacks without stale closures
  const favoritesRef = useRef<Set<string>>(favorites)
  favoritesRef.current = favorites

  // Load favorites from Firestore
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      console.log('[Favorites] Skipping load — not authenticated', {
        isAuthenticated,
        userId: user?.id ?? 'undefined',
      })
      setFavorites(new Set())
      return
    }

    const loadFavorites = async () => {
      setLoading(true)
      setError(null)
      const favPath = `users/${user.id}/data/favorites`
      console.log(`[Favorites] Loading from path: ${favPath}`)
      console.log(`[Favorites] User ID: ${user.id}`)
      console.log(`[Favorites] Firestore instance exists: ${!!db}`)

      try {
        const favRef = doc(db, 'users', user.id, 'data', 'favorites')
        console.log('[Favorites] Document reference created, calling getDoc...')
        const favDoc = await getDoc(favRef)
        console.log(
          `[Favorites] getDoc completed. Document exists: ${favDoc.exists()}`,
        )

        if (favDoc.exists()) {
          const data = favDoc.data()
          const ids = data.productIds || []
          console.log(`[Favorites] Loaded ${ids.length} favorites:`, ids)
          setFavorites(new Set(ids))
        } else {
          console.log(
            `[Favorites] No favorites doc at ${favPath} — starting fresh`,
          )
          setFavorites(new Set())
        }
      } catch (err: any) {
        const errorCode = err?.code || 'unknown'
        const errorMessage = err?.message || String(err)
        console.error(`[Favorites] ❌ LOAD FAILED from ${favPath}`)
        console.error(`[Favorites] Error code: ${errorCode}`)
        console.error(`[Favorites] Error message: ${errorMessage}`)
        console.error(`[Favorites] Full error:`, err)
        setError(`Load failed: ${errorCode} — ${errorMessage}`)
      } finally {
        setLoading(false)
      }
    }

    loadFavorites()
  }, [isAuthenticated, user?.id])

  // Toggle favorite with atomic Firestore update
  const toggleFavorite = useCallback(
    async (productId: string) => {
      if (!isAuthenticated || !user?.id) {
        console.warn('[Favorites] ⚠️ Cannot toggle — user not authenticated', {
          isAuthenticated,
          userId: user?.id ?? 'undefined',
        })
        return
      }

      const favPath = `users/${user.id}/data/favorites`
      const favRef = doc(db, 'users', user.id, 'data', 'favorites')
      // Use ref to get current state (avoids stale closure)
      const isCurrentlyFavorited = favoritesRef.current.has(productId)

      console.log(
        `[Favorites] ▶ Toggle: ${isCurrentlyFavorited ? 'REMOVING' : 'ADDING'} product "${productId}"`,
      )
      console.log(`[Favorites]   Path: ${favPath}`)
      console.log(`[Favorites]   User ID: ${user.id}`)
      console.log(
        `[Favorites]   Operation: ${isCurrentlyFavorited ? 'arrayRemove' : 'arrayUnion'}`,
      )

      // Optimistic UI update
      setFavorites((prev) => {
        const next = new Set(prev)
        if (isCurrentlyFavorited) {
          next.delete(productId)
        } else {
          next.add(productId)
        }
        return next
      })
      setError(null)

      // Persist to Firestore atomically
      try {
        console.log('[Favorites] Sending setDoc to Firestore...')
        if (isCurrentlyFavorited) {
          await setDoc(
            favRef,
            { productIds: arrayRemove(productId) },
            { merge: true },
          )
        } else {
          await setDoc(
            favRef,
            { productIds: arrayUnion(productId) },
            { merge: true },
          )
        }
        console.log(`[Favorites] ✅ Successfully saved to Firestore`)
      } catch (err: any) {
        const errorCode = err?.code || 'unknown'
        const errorMessage = err?.message || String(err)
        console.error(`[Favorites] ❌ WRITE FAILED`)
        console.error(`[Favorites] Error code: ${errorCode}`)
        console.error(`[Favorites] Error message: ${errorMessage}`)
        console.error(`[Favorites] Full error:`, err)

        // Specific guidance based on error code
        if (errorCode === 'permission-denied') {
          console.error(
            `[Favorites] 🔒 PERMISSION DENIED — Firestore rules are blocking this write.`,
          )
          console.error(
            `[Favorites] Fix: Deploy rules with subcollection access:`,
          )
          console.error(`[Favorites]   match /users/{userId}/data/{docId} {`)
          console.error(
            `[Favorites]     allow read, write: if request.auth.uid == userId;`,
          )
          console.error(`[Favorites]   }`)
          console.error(
            `[Favorites] Deploy: node firebase/deployRules.js --firestore`,
          )
        } else if (
          errorCode.includes('appCheck') ||
          errorCode.includes('app-check') ||
          errorMessage.includes('App Check')
        ) {
          console.error(`[Favorites] 🛡️ APP CHECK BLOCKED this request.`)
          console.error(
            `[Favorites] Fix: Add debug token or disable App Check enforcement in Firebase Console.`,
          )
        } else if (
          errorCode === 'unavailable' ||
          errorCode === 'deadline-exceeded'
        ) {
          console.error(
            `[Favorites] 🌐 NETWORK ERROR — Firestore is unreachable.`,
          )
        }

        setError(`Write failed: ${errorCode} — ${errorMessage}`)

        // Revert on error
        setFavorites((prev) => {
          const reverted = new Set(prev)
          if (isCurrentlyFavorited) {
            reverted.add(productId)
          } else {
            reverted.delete(productId)
          }
          return reverted
        })
      }
    },
    [isAuthenticated, user?.id],
  )

  const isFavorited = useCallback(
    (productId: string) => favorites.has(productId),
    [favorites],
  )

  return {
    favorites,
    toggleFavorite,
    isFavorited,
    favoritesCount: favorites.size,
    loading,
    error,
  }
}
